﻿import React, { useState, useCallback, useRef, useMemo } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { UploadTab } from "./tabs/UploadTab";
import { MapTab } from "./tabs/MapTab";
import { DownloadTab } from "./tabs/DownloadTab";
import { ImportFeedbackTab } from "./tabs/ImportFeedbackTab";
import { HistoryTab } from "./tabs/HistoryTab";
import { IMPORT_TYPES, ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys";
import { AsyncTaskUI, useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  extractAsyncTaskId,
  getTaskStatusMessage,
  isAsyncTaskType,
  matchImportEditorCsvTask,
  resolveImportDataTypeId,
  toCsvDownloadBlob,
} from "@/modern/components/async-tasks/utils";
import { downloadFileFromData, showToast } from "@/other/utils/utils";

const ARCHIVED_EXPORT_TYPES = [
  IMPORT_TYPES.tasks,
  IMPORT_TYPES.resources,
  IMPORT_TYPES.clients,
  IMPORT_TYPES.taskBudget
];

interface ImportEditorProps {
  isShow: boolean;
  context: any;
  record: {
    canEdit: boolean;
    importType: string;
    labelIndex?: number;
    asyncTaskImportResult?: any;
    importHeader: string;
  };
  onClose: () => void;
  editorInstance: any;
}

interface ImportEditorContentProps {
  context: any;
  record: ImportEditorProps["record"];
  canEdit: boolean;
  importType: string;
  labelIndex: number;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  mapTabVisible: boolean;
  importFeedbackTabVisible: boolean;
  importResponseData: any;
  selectedFiles: File[];
  uploadTabRef: React.RefObject<any>;
  onBeforeImport: (files: File[]) => void;
  onAfterRecordsImport: (data: any, viewImportType: string) => void;
  onImportSubmitted: (data: unknown, fileName: string) => void;
  onExportSubmitted: (data: unknown, fileName: string) => void;
  onUndoSubmitted: (data: unknown, importBatchId: number) => void;
}

function ImportEditorContent({
  context,
  record,
  canEdit,
  importType,
  labelIndex,
  activeTab,
  setActiveTab,
  mapTabVisible,
  importFeedbackTabVisible,
  importResponseData,
  selectedFiles,
  uploadTabRef,
  onBeforeImport,
  onAfterRecordsImport,
  onImportSubmitted,
  onExportSubmitted,
  onUndoSubmitted,
}: ImportEditorContentProps) {
  const { taskMatching } = useAsyncTaskUIContext();

  const isImportPending = taskMatching((task) =>
    isAsyncTaskType(task, ASYNC_TASK_TYPES.CSV_IMPORT),
  );
  const isExportPending = taskMatching((task) =>
    isAsyncTaskType(task, ASYNC_TASK_TYPES.CSV_EXPORT),
  );
  const isUndoPendingForBatch = useCallback(
    (importBatchId: number) =>
      taskMatching((task) => {
        if (!isAsyncTaskType(task, ASYNC_TASK_TYPES.UNDO_CSV_IMPORT)) return false;
        const meta = task.meta_data as { import_batch_id?: number };
        return meta.import_batch_id === importBatchId;
      }),
    [taskMatching],
  );

  return (
    <BaseModalBody className="!p-0 flex flex-col">
      <AsyncTaskUI.Banner className="mx-4 mt-2" />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
        <TabsList className="w-full justify-start rounded-none border-b bg-background p-1 h-auto">
          <TabsTrigger value="upload" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
            Import
          </TabsTrigger>
          {mapTabVisible && (
            <TabsTrigger value="map" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Map
            </TabsTrigger>
          )}
          <TabsTrigger value="download" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
            Export
          </TabsTrigger>
          {importFeedbackTabVisible && (
            <TabsTrigger value="feedback" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Import Feedback
            </TabsTrigger>
          )}
          <TabsTrigger value="history" className="rounded-none px-6 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="flex-1 data-[state=inactive]:hidden" forceMount>
          <UploadTab
            ref={uploadTabRef}
            record={record}
            context={context}
            canEdit={canEdit}
            isImportPending={isImportPending}
            onBeforeImport={onBeforeImport}
            onAfterImport={(data) => onAfterRecordsImport(data, importType)}
            onImportSubmitted={onImportSubmitted}
          />
        </TabsContent>

        {mapTabVisible && (
          <TabsContent value="map" className="flex-1 data-[state=inactive]:hidden">
            <MapTab
              record={record}
              context={context}
              files={selectedFiles}
              onFinalizeImport={(file) => uploadTabRef.current?.importEntity(file)}
            />
          </TabsContent>
        )}

        <TabsContent value="download" className="flex-1 data-[state=inactive]:hidden">
          <DownloadTab
            record={record}
            context={context}
            isEntityArchive={ARCHIVED_EXPORT_TYPES.includes(record.importType)}
            isExportPending={isExportPending}
            onExportSubmitted={onExportSubmitted}
          />
        </TabsContent>

        {importFeedbackTabVisible && (
          <TabsContent value="feedback" className="flex-1 data-[state=inactive]:hidden">
            <ImportFeedbackTab importResponseData={importResponseData} labelIndex={labelIndex} />
          </TabsContent>
        )}

        <TabsContent value="history" className="flex-1 data-[state=inactive]:hidden">
          <HistoryTab
            record={record}
            context={context}
            isUndoPendingForBatch={isUndoPendingForBatch}
            onUndoSubmitted={onUndoSubmitted}
          />
        </TabsContent>
      </Tabs>
    </BaseModalBody>
  );
}

export const ImportEditor: React.FC<ImportEditorProps> = ({
  context,
  record,
  onClose,
  editorInstance
}) => {
  const [activeTab, setActiveTab] = useState<string>("upload");
  const [mapTabVisible, setMapTabVisible] = useState<boolean>(false);
  const [importFeedbackTabVisible, setImportFeedbackTabVisible] = useState<boolean>(false);
  const [importResponseData, setImportResponseData] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const uploadTabRef = useRef<any>(null);
  const pendingExportFileNameRef = useRef("export.csv");

  const { pinTask, fetchResult } = useAsyncTaskContext();

  const { canEdit, importType, labelIndex = 1, asyncTaskImportResult, importHeader } = record;
  const boardId = context?.selectedBoard?.record?.id as number | undefined;
  const dataTypeId = resolveImportDataTypeId(importType);

  const taskFilter = useCallback(
    (task: AsyncTask) =>
      boardId != null &&
      dataTypeId != null &&
      matchImportEditorCsvTask(task, boardId, dataTypeId),
    [boardId, dataTypeId],
  );

  const selectUploadedView = useCallback(
    (viewImportType: string) => {
      const defaultViewOfImportedData = context.viewManager.getDefaultViewByType(viewImportType);
      if (defaultViewOfImportedData) {
        context.viewManager.selectView(defaultViewOfImportedData);
      }
    },
    [context],
  );

  const onAfterRecordsImport = useCallback(
    (importResult: any, viewImportType: string) => {
      const { created_count = 0, updated_count = 0 } = importResult ?? {};
      const totalImportedRecords = created_count + updated_count;

      setImportFeedbackTabVisible(true);
      setImportResponseData(importResult);

      if (totalImportedRecords > 0) {
        selectUploadedView(viewImportType);
      }
    },
    [selectUploadedView],
  );

  const onBeforeImport = useCallback(
    (files: File[]) => {
      if (IMPORT_TYPES.taskBudget === importType) {
        setSelectedFiles(files);
        setMapTabVisible(true);
        setActiveTab("map");
      }
    },
    [importType],
  );

  const handleImportSubmitted = useCallback(
    (data: unknown, fileName: string) => {
      if (boardId == null || dataTypeId == null) return;
      const taskId = extractAsyncTaskId(data);
      if (taskId == null) return;

      pinTask(taskId, {
        type: ASYNC_TASK_TYPES.CSV_IMPORT,
        meta_data: {
          board_id: boardId,
          data_type: dataTypeId,
          file_name: fileName,
        },
      });
    },
    [boardId, dataTypeId, pinTask],
  );

  const handleExportSubmitted = useCallback(
    (data: unknown, fileName: string) => {
      if (boardId == null || dataTypeId == null) return;
      const taskId = extractAsyncTaskId(data);
      if (taskId == null) return;

      pendingExportFileNameRef.current = fileName;
      pinTask(taskId, {
        type: ASYNC_TASK_TYPES.CSV_EXPORT,
        meta_data: {
          board_id: boardId,
          data_type: dataTypeId,
          file_name: fileName,
        },
      });
    },
    [boardId, dataTypeId, pinTask],
  );

  const handleUndoSubmitted = useCallback(
    (data: unknown, importBatchId: number) => {
      if (boardId == null || dataTypeId == null) return;
      const taskId = extractAsyncTaskId(data);
      if (taskId == null) return;

      pinTask(taskId, {
        type: ASYNC_TASK_TYPES.UNDO_CSV_IMPORT,
        meta_data: {
          board_id: boardId,
          data_type: dataTypeId,
          import_batch_id: importBatchId,
        },
      });
    },
    [boardId, dataTypeId, pinTask],
  );

  const asyncEffects = useMemo(
    () => ({
      onCompleted: (task: AsyncTask) => {
        const taskId = task.id;

        if (isAsyncTaskType(task, ASYNC_TASK_TYPES.CSV_IMPORT)) {
          void fetchResult(taskId)
            .then((result) => {
              if (
                result &&
                typeof result === "object" &&
                ("created_count" in (result as object) ||
                  "updated_count" in (result as object))
              ) {
                onAfterRecordsImport(result, importType);
              }
              showToast({
                msg: getTaskStatusMessage(task),
                type: "success",
                duration: 3000,
              });
            })
            .catch(() => {
              showToast({
                msg: getTaskStatusMessage(task),
                type: "success",
                duration: 3000,
              });
            });
          return;
        }

        if (isAsyncTaskType(task, ASYNC_TASK_TYPES.CSV_EXPORT)) {
          void fetchResult(taskId)
            .then((result) => {
              const blob = toCsvDownloadBlob(result);
              if (blob) {
                downloadFileFromData(blob, pendingExportFileNameRef.current);
              }
              showToast({
                msg: getTaskStatusMessage(task),
                type: "success",
                duration: 3000,
              });
            })
            .catch(() => {
              showToast({
                msg: getTaskStatusMessage(task),
                type: "error",
                duration: 3000,
              });
            });
          return;
        }

        if (isAsyncTaskType(task, ASYNC_TASK_TYPES.UNDO_CSV_IMPORT)) {
          showToast({
            msg: getTaskStatusMessage(task),
            type: "success",
            duration: 3000,
          });
        }
      },
      onFailed: (task: AsyncTask) => {
        if (
          isAsyncTaskType(task, ASYNC_TASK_TYPES.CSV_IMPORT) ||
          isAsyncTaskType(task, ASYNC_TASK_TYPES.CSV_EXPORT) ||
          isAsyncTaskType(task, ASYNC_TASK_TYPES.UNDO_CSV_IMPORT)
        ) {
          showToast({
            msg: getTaskStatusMessage(task),
            type: "error",
            duration: 3000,
          });
        }
      },
    }),
    [fetchResult, importType, onAfterRecordsImport],
  );

  React.useEffect(() => {
    if (asyncTaskImportResult) {
      onAfterRecordsImport(asyncTaskImportResult, importType);
    }
  }, [asyncTaskImportResult, importType, onAfterRecordsImport]);

  const getTitle = () => {
    const tabTitles: Record<string, string> = {
      upload: "Import",
      map: "Map",
      download: "Export",
      feedback: "Import Feedback",
      history: "History"
    };
    const action = tabTitles[activeTab] || "Import / Export";
    return `${action} ${importHeader} From CSV file`;
  };

  if (boardId == null || dataTypeId == null) {
    return null;
  }

  return (
    <BaseModal
      record={record}
      onClose={onClose}
      title={getTitle()}
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "min(93em, 100vw)",
        height: "min(800px, 100vh)",
        minWidth: "30em",
        maxWidth: "100vw",
        minHeight: "600px",
        maxHeight: "100vh"
      }}
      modal={true}
      className="resize"
    >
      <AsyncTaskUI filter={taskFilter} effects={asyncEffects}>
        <ImportEditorContent
          context={context}
          record={record}
          canEdit={canEdit}
          importType={importType}
          labelIndex={labelIndex}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          mapTabVisible={mapTabVisible}
          importFeedbackTabVisible={importFeedbackTabVisible}
          importResponseData={importResponseData}
          selectedFiles={selectedFiles}
          uploadTabRef={uploadTabRef}
          onBeforeImport={onBeforeImport}
          onAfterRecordsImport={onAfterRecordsImport}
          onImportSubmitted={handleImportSubmitted}
          onExportSubmitted={handleExportSubmitted}
          onUndoSubmitted={handleUndoSubmitted}
        />
      </AsyncTaskUI>
    </BaseModal>
  );
};
